/* tslint:disable */
// @ts-disable

import React, {
  useEffect,
  forwardRef,
  useState,
} from 'react';
import _ from 'lodash';
import {Formik, Form, useFormikContext} from 'formik';
import * as Yup from 'yup';
import {Link} from 'react-router-dom';
import {useTranslateFunction} from 'contexts/translation/TranslationContext';
import {useCourseTheme} from 'course/contexts/ThemeContext';
import styled from 'styled-components';

/**
 * Here is example formData

const formData = [
  {
    name: "name",
    input: NameInput,
    default:"",
    validation:Yup.string().trim().min(3).required("Name is required"),
    postProcess: (value) => value.trim(),
  }
]
 */

const AutoSubmitToken = ({debounceMs}) => {
  // Grab values and submitForm from context
  const {values, submitForm} = useFormikContext();

  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const debouncedSubmit = React.useCallback(
    _.debounce(submitForm, debounceMs), // debounce; default 300 ms
    [], // dependencies array
  );

  React.useEffect(() => {
    // Call the debounced version of submitForm
    debouncedSubmit();
    // Cancel the debounce timer on cleanup to immediately call any waiting submitForm
    return debouncedSubmit.cancel;
  }, [values, debouncedSubmit]);

  return null;
};

export const FormixFormWithData = ({
  serverData,
  backUrl,
  formFormat,
  onSubmit,
  showGlobalError = true,
  autoSave = false,
  debounceMs = 300,
  submitButtonInner = null,
  submitButtonTestId = null,
  customDisableSubmit = false,
  // Optional class applied to the rendered <Form>. Consumers opt into
  // form-level styling (e.g. `form-field-stack`) here; this component has many
  // consumers, so it must not impose a layout class on all of them by default.
  className = '',
}) => {
  const t = useTranslateFunction('miscUI');
  const {buttonColorPrimary} = useCourseTheme();

  const onFormikSubmit = values => {
    // apply the post process on data
    const data = formFormat.reduce((acc, formItem) => {
      const key = formItem.name;
      const value = values[key];
      const postProcess = formItem.postProcess;
      if (postProcess) {
        const processedValue = postProcess ? postProcess(value) : value;
        return {...acc, [key]: processedValue};
      }
      return {...acc, [key]: value};
    }, {});
    onSubmit(data);
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const [initValues, setInitValues] = useState(
    // eslint-disable-next-line @eslint-react/prefer-use-state-lazy-initialization -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    getInitialValues(formFormat, serverData),
  );
  return (
    <Formik
      enableReinitialize
      initialValues={initValues}
      validationSchema={getValidationSchema(formFormat)}
      onSubmit={onFormikSubmit}
    >
      {({
        values,
        errors,
        touched,
        setFieldValue,
        setFieldTouched,
        validateForm,
      }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
        useEffect(() => {
          validateForm();
          // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
        }, []);
        return (
          <Form className={className} style={{width: '100%'}} autoComplete="off">
            {autoSave ? <AutoSubmitToken debounceMs={debounceMs} /> : <></>}
            {formFormat.map((formItem, index) => {
              if (!formItem.input) {
                // Render nothing (not an empty <div>) so a fieldless entry does
                // not become a flex child that consumes a `gap` in layouts like
                // `form-field-stack`.
                return null;
              }
              const InputComponent = formItem.input;
              return (
                <div key={formItem.name || index}>
                  <InputComponent
                    values={values}
                    setFieldValue={setFieldValue}
                    errors={errors}
                    touched={touched}
                    setFieldTouched={setFieldTouched}
                    {...formItem}
                  />
                </div>
              );
            })}

            {/* <hr/> */}

            {Object.keys(errors).length !== 0 && showGlobalError ? (
              <div className="mt-4" style={{color: buttonColorPrimary}}>
                {t(`Can't save yet.`)}{' '}
                {getFormErrorMessage(formFormat, errors, t)}
              </div>
            ) : null}
            {/* if we are doing auto save we do not need a save button */}
            {autoSave ? (
              <></>
            ) : (
              <div className="formActionContainer">
                <SubmitButton
                  color={buttonColorPrimary}
                  className="btn btn-primary"
                  type="submit"
                  data-testid={submitButtonTestId}
                  disabled={
                    Object.keys(errors).length !== 0 || customDisableSubmit
                  }
                >
                  {submitButtonInner ? submitButtonInner : t('Submit')}
                </SubmitButton>

                {backUrl && <BackButton url={backUrl} />}
              </div>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

interface HandleSubmitFormixFormWithDataProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  serverData: any;
  backUrl: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  formFormat: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  onSubmit: (data: any) => void | Promise<void>;
  showGlobalError?: boolean;
  /**
   * Optional class applied to the rendered `<Form>`. Consumers opt into
   * form-level styling here; omitted by default so this shared component does
   * not impose a layout class on all of its consumers.
   */
  className?: string;
}

export const HandleSubmitFormixFormWithData = forwardRef<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  any,
  HandleSubmitFormixFormWithDataProps
>(
  (
    {serverData, formFormat, onSubmit, showGlobalError = true, className = ''},
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    ref,
  ) => {
    const onFormikSubmit = values => {
      // apply the post process on data
      const data = formFormat.reduce((acc, formItem) => {
        const key = formItem.name;
        const value = values[key];
        const postProcess = formItem.postProcess;
        if (postProcess) {
          const processedValue = postProcess ? postProcess(value) : value;
          return {...acc, [key]: processedValue};
        }
        return {...acc, [key]: value};
      }, {});
      onSubmit(data);
    };
    const initValues = getInitialValues(formFormat, serverData);
    const t = useTranslateFunction('miscUI');
    const {buttonColorPrimary} = useCourseTheme();

    return (
      <Formik
        // Use the forwarded ref so callers can imperatively call submitForm()
        // and read validation errors without creating another internal ref.
        innerRef={ref}
        enableReinitialize
        initialValues={initValues}
        validationSchema={getValidationSchema(formFormat)}
        onSubmit={onFormikSubmit}
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          setFieldTouched,
          validateForm,

          // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
          submitForm,
        }) => {
          // eslint-disable-next-line react-hooks/rules-of-hooks -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
          useEffect(() => {
            validateForm();
            // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
          }, []);

          return (
            <Form
              className={className}
              style={{width: '100%'}}
              autoComplete="off"
            >
              {formFormat.map((formItem, index) => {
                const key = formItem.name;
                const InputComponent = formItem.input;
                return (
                  // eslint-disable-next-line @eslint-react/no-array-index-key -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
                  <div key={index}>
                    <InputComponent
                      name={key}
                      values={values}
                      setFieldValue={setFieldValue}
                      errors={errors}
                      touched={touched}
                      setFieldTouched={setFieldTouched}
                      {...formItem}
                    />
                  </div>
                );
              })}

              {/* <hr/> */}

              {Object.keys(errors).length !== 0 && showGlobalError ? (
                <div className="mt-4" style={{color: buttonColorPrimary}}>
                  {t(`Can't save yet.`)}{' '}
                  {getFormErrorMessage(formFormat, errors, t)}
                </div>
              ) : null}
            </Form>
          );
        }}
      </Formik>
    );
  },
);

const BackButton = ({url}) => {
  const t = useTranslateFunction('miscUI');
  return (
    <Link to={url} className="btn btn-secondary ml-2">
      {t('Back')}
    </Link>
  );
};

export const getFormErrorMessage = (formFormat, errors, t) => {
  // eslint-disable-next-line no-var -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  for (var itemData of formFormat) {
    const key = itemData.name;
    if (key in errors) {
      // could be a dictionary
      const errorData = errors[key];
      if (typeof errorData === 'object') {
        // eslint-disable-next-line no-var -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
        for (var errorKey in errorData) {
          return errorData[errorKey] + '.';
        }
      }
      return errors[key] + '.';
    }
  }
  console.log(errors);
  return t('unknown error');
};

const getValidationSchema = formFormat => {
  const schemaData = {};
  // eslint-disable-next-line no-var -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  for (var formItem of formFormat) {
    const validation = formItem.validation;
    if (validation) {
      schemaData[formItem.name] = validation;
    }
  }
  return Yup.object(schemaData);
};

const getInitialValues = (formFormat, serverData) => {
  const initValues = {};
  // eslint-disable-next-line no-var -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  for (var formItem of formFormat) {
    const key = formItem.name;
    if (
      key in serverData &&
      serverData[key] !== undefined &&
      serverData[key] !== null
    ) {
      initValues[key] = serverData[key];
    } else if (formItem.default !== undefined) {
      initValues[key] = formItem.default;
    } else {
      initValues[key] = '';
    }
  }
  return initValues;
};

const SubmitButton = styled.button`
  background-color: ${props => props.color};
  border-color: ${props => props.color};
  color: white;

  &:hover:not(:disabled) {
    background-color: ${props => props.color};
    border-color: ${props => props.color};
    filter: brightness(0.85);
  }

  /*
   * Default: disabled keeps the themed hue at full opacity (no dim). This is
   * load-bearing for the many consumers of FormixFormWithData — do not add a
   * fade here. Forms that want a disabled affordance scope it themselves; see
   * the styled wrapper in SendRequestForm.tsx for the pattern.
   */
  &:disabled {
    background-color: ${props => props.color};
    border-color: ${props => props.color};
    filter: brightness(1);
    opacity: 1;
  }
`;
